import type { Product } from '@clockwise/clients/products';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { productsApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  blankProductValues,
  handleProductMutationError,
  ProductFormFields,
  ProductFormSchema,
  type ProductFormValues,
  productPayloadFromValues,
} from './product-form-fields';

// Create AND edit share this one sheet — pass `product: null` for create,
// the loaded row for edit. Both the list's row action and the detail page's
// Edit button open this instead of the old `/setup/activities/$id/edit`
// full-page route, which existed only to duplicate this form and has been
// removed.
interface ProductFormSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
}

export function ProductFormSheet({ open, onOpenChange, product }: ProductFormSheetProps) {
  const isEdit = product !== null;
  const queryClient = useQueryClient();

  const form = useForm<ProductFormValues>({
    resolver: zodResolver(ProductFormSchema),
    defaultValues: blankProductValues,
  });

  useEffect(() => {
    if (!open) return;
    if (product) {
      form.reset({
        retainerCode: product.retainerCode ?? '',
        tmCode: product.tmCode ?? '',
        name: product.name,
        description: product.description ?? '',
        serviceRoleId: product.serviceRoleId,
        visibleToServiceRoleIds: product.visibleToServiceRoleIds,
        internal: product.internal,
        excludeFromBudget: product.excludeFromBudget,
      });
    } else {
      form.reset(blankProductValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product?.id]);

  const mutation = useMutation({
    mutationFn: async (values: ProductFormValues) => {
      const body = productPayloadFromValues(values);
      return isEdit && product ? productsApi.patch(product.id, body) : productsApi.create(body);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Activity updated' : 'Activity created');
      queryClient.invalidateQueries({ queryKey: queryKeys.products.all });
      onOpenChange(false);
    },
    onError: (err) => handleProductMutationError(form, err),
  });

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));

  const { requestOpenChange, discardDialog } = useUnsavedChangesGuard({
    open,
    isDirty: form.formState.isDirty,
    isSubmitting: mutation.isPending,
    onOpenChange,
  });

  return (
    <>
      <Sheet open={open} onOpenChange={requestOpenChange}>
        <SheetContent className="flex flex-col gap-0 sm:max-w-md">
          <SheetHeader>
            <SheetTitle>{isEdit ? 'Edit activity' : 'Add activity'}</SheetTitle>
            <SheetDescription>
              {isEdit ? 'Edit this activity. Changes apply immediately.' : 'Create a new activity.'}
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto px-4">
              <ProductFormFields form={form} />
            </div>

            <SheetFooter className="flex-row justify-end border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => requestOpenChange(false)}
                disabled={mutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? 'Saving…' : isEdit ? 'Save' : 'Create activity'}
              </Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>
      {discardDialog}
    </>
  );
}
